import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { useFormik } from 'formik';
import * as yup from 'yup';
import { FormControl, FormLabel, WrapItem, Tooltip, useToast, Heading, Button, Box, Flex, Container, Text, useDisclosure } from "@chakra-ui/react"
import { Helmet } from 'react-helmet-async'
import { useEffect } from "react"
import { updateUserPreferences, reset } from '../features/auth/authSlice'

import LoadingOverlay from "../components/LoadingOverlay"
import SubscriptionManager from "../components/preferences/SubscriptionManager"
import HomeCountrySelector from "../components/HomeCountrySelector"
import MapBackgroundSelector from "./page_elements/MapBackgroundSelector"
import EmailVerificationView from "./page_elements/EmailVerificationView"
import CheckboxFormControl from "../components/preferences/CheckboxFormControl";
import ProfileManager from "../components/preferences/ProfileManager";
import PlanSelectionModal from "../components/preferences/PlanSelectionModal";

export default function Profile() {
  
  const { user, updatePreferencesRequest } = useSelector((state) => state.auth)

  const { isOpen, onOpen, onClose } = useDisclosure() // Plan modal

  const [searchParams] = useSearchParams()

  const [permitHomeCountrySwitching, setPermitHomeCountrySwitching] = useState(false)
  const [permitCustomMapBackground, setPermitCustomMapBackground] = useState(false)

  const formik = useFormik({
    initialValues: {
      isOperatorEnabled: false,
      promotionalEmailsConsent: false,
      enablePublicProfile: false,

      selectedCountry: undefined,
      selectedBackground: undefined,
      displayName: '',
    },
    validationSchema: () => {
      let schema = {
        isOperatorEnabled: yup.boolean(),
        promotionalEmailsConsent: yup.boolean(),
        enablePublicProfile: yup.boolean(),
  
        selectedCountry: yup.mixed(),
        selectedBackground: yup.mixed(),
      };
  
      // Conditional validation for displayName
      if (formik.values.enablePublicProfile) {
        schema.displayName = yup.string()
          .matches(/^[a-zA-Z0-9_-]{5,30}$/, 'Display name must be between 5 and 30 characters long and can only contain letters, numbers, hyphens and underscores.')
          .required('Display name is required.');
      } else {
        schema.displayName = yup.string();
      }
  
      return yup.object(schema);
    },
    onSubmit: (values) => {
      dispatch(updateUserPreferences({
        isOperatorEnabled: values.isOperatorEnabled,
        promotionalEmailsConsent: values.promotionalEmailsConsent,
        enablePublicProfile: values.enablePublicProfile,
        displayName: values.displayName ? values.displayName : undefined,
        homeCountryCode: values.selectedCountry ? values.selectedCountry.code : undefined,
        selectedBackground: values.selectedBackground ? values.selectedBackground : undefined
      }))
    },
  });

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const toast = useToast()

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    formik.setValues({
      isOperatorEnabled: user.preferences.isOperatorEnabled,
      promotionalEmailsConsent: user.preferences.promotionalEmailsConsent,
      enablePublicProfile: user.preferences.publicProfile.enabled,
      selectedCountry: user.details.homeCountry,
      selectedBackground: user.preferences.selectedBackground ? user.preferences.selectedBackground : 'default',
      displayName: user.preferences.publicProfile ? user.preferences.publicProfile.displayName : undefined,
    })

    if (user.details.subscription.tier !== 'free-standard' && user.details.subscription.tier !== 'free-restricted') {
      setPermitHomeCountrySwitching(true)
      setPermitCustomMapBackground(true)
    }

    return () => {
      dispatch(reset())
    }
  }, [user, navigate])

  useEffect(() => {
    if (updatePreferencesRequest.isSuccess)
      toast({ title: 'Successfully updated preferences!', status: 'success', duration: 4000, isClosable: true, })

    if (updatePreferencesRequest.isError)
      toast({ title: 'Error updating preferences', status: 'error', description: `Error: ${updatePreferencesRequest.message}`, duration: 8000, isClosable: true, })

    return () => {
      dispatch(reset())
    }
  }, [updatePreferencesRequest.isSuccess, updatePreferencesRequest.isError])

  // Toggle plan modal from pricing ref
  useEffect(() => {
    if (searchParams.get('selectedPlan') === 'true' &&
      (user.details.subscription.tier === 'free-standard' || user.details.subscription.tier === 'free-restricted')
    ) {
      onOpen()
    }
  }, [searchParams])

  if (updatePreferencesRequest.isLoading && !user) {
    return <LoadingOverlay type="full" />
  }

  return (
    <>
      <Helmet>
        <title>Account & preferences | TrainLookout</title>
        <meta name="description" content="Your preferences page" />
        <meta name="keywords" content="account, preferences, settings" />
        <link rel="canonical" href="/preferences" />
      </Helmet>

      <EmailVerificationView />

      {/* Main preferences content */}
      <Container maxW="container.xl" centerContent p={4}>
        <Heading as="h1" size="lg" mb={4}>Account & preferences</Heading>

        <Flex direction={{ base: "column", md: "row" }} w="60%">
          <Box flex="1" overflowY="auto" maxWidth={{ md: "60%" }} pr={{ md: 4 }} mb={{ base: 4, md: 0 }}>
            <Heading as="h2" size="md" mb={4}>Subscription:</Heading>
            <SubscriptionManager />

            <Heading as="h2" size="md" mb={4} pt={2}>Account preferences:</Heading>
            <CheckboxFormControl
              label="Receive email updates from TrainLookout:"
              name="promotionalEmailsConsent"
              onChange={formik.handleChange}
              isChecked={formik.values.promotionalEmailsConsent}
              mb={4}
            />

            <Heading as="h2" size="md" mb={4}>My journeys preferences:</Heading>

            <WrapItem>
              <Tooltip
                label={user.details.subscription.tier === 'free-standard' && 'Your free tier only allows journeys within, to or from your home country. Unlimited, worldwide journeys or changing your home country require an All-Country Pass.'}
                closeDelay={500}
                isDisabled={permitCustomMapBackground}
              >
                <FormControl display="flex" alignItems="center" width="100%" mb={4}>
                  <Flex justifyContent="space-between" width="100%">
                    <FormLabel mb="0" flexShrink={0} fontWeight="normal">Home country:</FormLabel>
                    <HomeCountrySelector
                      isDisabled={!permitHomeCountrySwitching}
                      preselectedCountry={formik.values.selectedCountry ? formik.values.selectedCountry.name : undefined}
                      setSelectedCountry={(country) => formik.setFieldValue('selectedCountry', country)}
                    />
                  </Flex>
                </FormControl>
              </Tooltip>
            </WrapItem>

            <CheckboxFormControl
              label="Enable 'Operator' field:"
              name="isOperatorEnabled"
              onChange={formik.handleChange}
              isChecked={formik.values.isOperatorEnabled}
              mb={4}
            />

            <CheckboxFormControl
              label="Enable public profile:"
              name="enablePublicProfile"
              onChange={formik.handleChange}
              isChecked={formik.values.enablePublicProfile}
              mb={4}
            />
            <Text mb={4} fontSize="sm" color="gray.500">
              A public profile features a map of your journeys and select insights, including total distance, duration, and most visited stations. This is displayed on a personal profile page, accessible through your chosen display name. <br/>
              Only the minimum data required will be accessible to draw your journey map (excluding upcoming journeys) and to generate basic insights. <br/>
              Details of individual journeys, including dates, notes, trips and other information, remain private and only accessible to you. You may disable your public profile at any time.
            </Text>

            {formik.values.enablePublicProfile === true && (
              <ProfileManager formik={formik} />
            )}

            <MapBackgroundSelector
              permitCustomMapBackground={permitCustomMapBackground}
              selectedBackground={formik.values.selectedBackground}
              setSelectedBackground={(background) => formik.setFieldValue('selectedBackground', background)}
            />
          </Box>

          <Box position="sticky" top="0" alignSelf="start" p={4}>
            <Button
              colorScheme="green"
              onClick={formik.handleSubmit}
              isLoading={updatePreferencesRequest.isLoading}
              w="100%"
            >
              Save preferences
            </Button>
          </Box>
        </Flex>
      </Container>

      <PlanSelectionModal isOpen={isOpen} onClose={onClose} />
    </>
  )
}
